import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion';

import useAnimate, { entry } from '../ui/Motion';
import useCompanionHref from '../ui/companion';
import { ChevronIcon } from '../ui/icons';
import { site, contact, menu } from '../data/site';

const EASE = [0.16, 1, 0.3, 1];

/* Leaving is not the reverse of arriving. The sheet comes in on a spring
   that settles, and goes out on an accelerating curve that gets off the
   screen before the reader looks for the page behind it. */
const EASE_IN = [0.7, 0, 0.84, 0];

/* The mobile sheet, as one orchestrated move.
 *
 * The scrim dims the page, the panel travels in from the right edge, and
 * the rows chase it in from the same direction a beat later, so the whole
 * thing reads as one gesture arriving rather than four things fading up
 * in place. Closing runs it backwards and faster: the rows leave last
 * first, and the panel is gone in a third of a second.
 *
 * Reduced motion keeps the orchestration and drops the travel. Nothing
 * slides; the same elements arrive in the same order by opacity alone. */
function sheetMotion(animate) {
    if (!animate) {
        return {
            scrim: { closed: { opacity: 0 }, open: { opacity: 1 } },
            panel: { closed: { opacity: 0 }, open: { opacity: 1 } },
            list: { closed: {}, open: { transition: { staggerChildren: 0.03 } } },
            row: { closed: { opacity: 0 }, open: { opacity: 1 } }
        };
    }

    return {
        scrim: {
            closed: { opacity: 0, transition: { duration: 0.25, ease: EASE_IN } },
            open: { opacity: 1, transition: { duration: 0.4, ease: EASE } }
        },
        panel: {
            closed: { x: '100%', transition: { duration: 0.34, ease: EASE_IN } },
            open: {
                x: 0,
                transition: { type: 'spring', stiffness: 210, damping: 30, mass: 0.9 }
            }
        },
        list: {
            /* The rows are already travelling with the panel that holds
               them, so their own stagger starts once it is most of the
               way across rather than at the same instant. */
            closed: { transition: { staggerChildren: 0.035, staggerDirection: -1 } },
            open: { transition: { delayChildren: 0.17, staggerChildren: 0.06 } }
        },
        row: {
            closed: { opacity: 0, x: 26, transition: { duration: 0.18, ease: EASE_IN } },
            open: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.5, ease: EASE }
            }
        }
    };
}

/* The bar, assembled from `menu`.

   An item with a `group` belongs to that dropdown, and its `section`
   becomes a labelled column inside the dropdown's panel, so one trigger
   can carry both what we do and where we do it without the bar growing
   a button per column. An item with no `group` sits directly on the bar.

   A group holding a single destination collapses to a flat link: a
   dropdown with one item underneath it is a link with extra steps, and
   it is the only reason the three sites' bars would otherwise differ.

   Order is first-appearance throughout, so the data file reads the way
   the bar reads, left to right. */
function buildBar(items) {
    const bar = [];

    for (const item of items) {
        if (!item.group) {
            bar.push({ type: 'link', item });
            continue;
        }

        let group = bar.find(entry => entry.type === 'group' && entry.label === item.group);

        if (!group) {
            group = { type: 'group', label: item.group, columns: [] };
            bar.push(group);
        }

        const label = item.section || item.group;
        const column = group.columns.find(c => c.label === label);

        if (column) column.items.push(item);
        else group.columns.push({ label, items: [item] });
    }

    return bar.map(entry => {
        if (entry.type !== 'group') return entry;

        const items = entry.columns.flatMap(column => column.items);

        if (items.length === 1) return { type: 'link', item: { ...items[0], name: entry.label } };

        /* A lone column named after its own trigger needs no heading:
           the button directly above it already says that word. */
        const headed = entry.columns.length > 1 || entry.columns[0].label !== entry.label;

        return { ...entry, items, headed };
    });
}

/* The sheet stays one flat list of labelled sections rather than the
   bar's nested shape: a phone has the vertical room to show every
   heading at once, and nothing to hover. */
function sheetGroups(items) {
    const groups = [];

    for (const item of items) {
        const label = item.section || 'More';
        const found = groups.find(g => g.label === label);

        if (found) found.items.push(item);
        else groups.push({ label, items: [item] });
    }

    /* Whatever has no section of its own is the tail of the sheet, not
       a bucket in the middle of the trades. */
    return [...groups.filter(g => g.label !== 'More'), ...groups.filter(g => g.label === 'More')];
}

/* An external destination has to stay an <a>: handing it to <Link> would
   have the router try to match it as a path and render the 404. */
function MenuLink({ item, className, children, onClick }) {
    const companion = useCompanionHref(item);

    if (item.companion) {
        return (
            <a className={className} href={companion} onClick={onClick}>
                {children}
            </a>
        );
    }

    if (item.external || /^https?:/.test(item.href)) {
        return (
            <a
                className={className}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClick}
            >
                {children}
            </a>
        );
    }

    return (
        <Link className={className} to={item.href} onClick={onClick}>
            {children}
        </Link>
    );
}

export default function Nav() {
    const [lifted, setLifted] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [openGroup, setOpenGroup] = useState(null);
    const closeTimer = useRef(null);
    const linksRef = useRef(null);
    const pressedBy = useRef(null);
    const { pathname } = useLocation();
    const { scrollY } = useScroll();
    const animate = useAnimate();

    const sheetAnim = sheetMotion(animate);
    const bar = buildBar(menu);
    const sheet = sheetGroups(menu);

    useMotionValueEvent(scrollY, 'change', value => setLifted(value > 24));

    useEffect(() => {
        document.body.classList.toggle('is-locked', menuOpen);
        return () => document.body.classList.remove('is-locked');
    }, [menuOpen]);

    /* A route change closes everything: the sheet covers the page it just
       navigated to, and a dropdown left open would hang over the new one. */
    useEffect(() => {
        setMenuOpen(false);
        setOpenGroup(null);
    }, [pathname]);

    useEffect(() => {
        const onKey = event => {
            if (event.key !== 'Escape') return;
            setMenuOpen(false);
            setOpenGroup(null);
        };

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => () => clearTimeout(closeTimer.current), []);

    /* Touch has no hover to leave, so an open panel would otherwise stay
       up until its trigger is tapped again. A press anywhere outside the
       bar closes it. */
    useEffect(() => {
        if (!openGroup) return undefined;

        const onDown = event => {
            if (linksRef.current && !linksRef.current.contains(event.target)) setOpenGroup(null);
        };

        document.addEventListener('pointerdown', onDown);
        return () => document.removeEventListener('pointerdown', onDown);
    }, [openGroup]);

    /* Hovering off a trigger and onto its own panel crosses a gap. Closing
       on `pointerleave` alone would shut the panel underneath the cursor
       on the way; a short grace period lets the pointer arrive. */
    const openNow = label => {
        clearTimeout(closeTimer.current);
        setOpenGroup(label);
    };

    const closeSoon = () => {
        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpenGroup(null), 140);
    };

    const isCurrent = href => pathname === href;
    const groupHasCurrent = group => group.items.some(item => isCurrent(item.href));

    /* The bar shows the short form where a name is written for the
       footer's column rather than for a 0.86rem pill. */
    const barLabel = item => item.short || item.name;

    return (
        <>
            <motion.header
                className={`nav${lifted ? ' nav--lifted' : ''}${openGroup ? ' nav--open' : ''}`}
                {...entry(animate, { y: -80, opacity: 0 })}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1, delay: 0.15, ease: EASE }}
            >
                <div className="nav__inner">
                    <Link className="nav__brand" to="/" aria-label={`${site.name}, home`}>
                        <img src={site.logo} alt={site.name} width="240" height="60" />
                    </Link>

                    <nav className="nav__links" aria-label="Main" ref={linksRef}>
                        {bar.map(slot => (slot.type === 'link' ? (
                            <div className="nav__item" key={slot.item.href}>
                                <MenuLink
                                    item={slot.item}
                                    className={`nav__link${
                                        isCurrent(slot.item.href) ? ' nav__link--current' : ''
                                    }`}
                                >
                                    {barLabel(slot.item)}
                                    {slot.item.badge && <span className="nav__badge">{slot.item.badge}</span>}
                                    {slot.item.external && <small>↗</small>}
                                </MenuLink>
                            </div>
                        ) : (
                            <div
                                className={`nav__item${slot.label === openGroup ? ' nav__item--open' : ''}`}
                                key={slot.label}
                                /* Only a mouse hovers. A tap also fires enter and
                                   leave, which would open the panel and then close
                                   it again the moment the finger lifts, so touch and
                                   pen rely on the click toggle instead. */
                                onPointerEnter={event => event.pointerType === 'mouse' && openNow(slot.label)}
                                onPointerLeave={event => event.pointerType === 'mouse' && closeSoon()}
                            >
                                <button
                                    type="button"
                                    className={`nav__link${groupHasCurrent(slot) ? ' nav__link--current' : ''}`}
                                    aria-expanded={openGroup === slot.label}
                                    onPointerDown={event => {
                                        pressedBy.current = event.pointerType;
                                    }}
                                    onClick={() => {
                                        const pressed = pressedBy.current;
                                        pressedBy.current = null;
                                        /* A hovering mouse has already opened the panel,
                                           so its click should not be what shuts it. */
                                        if (pressed === 'mouse') return openNow(slot.label);
                                        setOpenGroup(open => (open === slot.label ? null : slot.label));
                                    }}
                                    /* Keyboard users tab to the trigger rather than
                                       hovering it, so focus has to open the panel too
                                       or its links are unreachable without a click. */
                                    onFocus={() => !pressedBy.current && openNow(slot.label)}
                                >
                                    {slot.label}
                                    <ChevronIcon className="nav__caret" />
                                </button>

                                <AnimatePresence>
                                    {openGroup === slot.label && (
                                        <motion.div
                                            className={`nav__panel${
                                                slot.columns.length > 1 ? ' nav__panel--split' : ''
                                            }`}
                                            initial={{ opacity: 0, y: -8, x: '-50%', scale: 0.97 }}
                                            animate={{ opacity: 1, y: 0, x: '-50%', scale: 1 }}
                                            exit={{ opacity: 0, y: -8, x: '-50%', scale: 0.97 }}
                                            transition={{ duration: 0.28, ease: EASE }}
                                        >
                                            {slot.columns.map(column => (
                                                <div className="nav__panel-col" key={column.label}>
                                                    {slot.headed && (
                                                        <span className="nav__panel-label">{column.label}</span>
                                                    )}

                                                    <ul>
                                                        {column.items.map(item => (
                                                            <li key={item.href}>
                                                                <MenuLink
                                                                    item={item}
                                                                    className="nav__panel-link"
                                                                    onClick={() => setOpenGroup(null)}
                                                                >
                                                                    {item.name}
                                                                    {item.badge && <span className="nav__badge">{item.badge}</span>}
                                                                    {item.external && <small>↗</small>}
                                                                </MenuLink>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )))}
                    </nav>

                    <div className="nav__actions">
                        <a className="nav__phone" href={contact.phoneHref}>{contact.phone}</a>
                        <Link className="btn btn--accent nav__cta" to="/#contact">Get a quote</Link>
                        <button
                            className={`nav__burger${menuOpen ? ' is-open' : ''}`}
                            onClick={() => setMenuOpen(open => !open)}
                            aria-expanded={menuOpen}
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        >
                            <span /><span />
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        className="nav-sheet"
                        initial="closed"
                        animate="open"
                        exit="closed"
                    >
                        {/* Dimming the page is half of what says the sheet is
                            on top of it; the other half is that pressing the
                            page puts the sheet away. */}
                        <motion.div
                            className="nav-sheet__scrim"
                            variants={sheetAnim.scrim}
                            onClick={() => setMenuOpen(false)}
                            aria-hidden="true"
                        />

                        <motion.div className="nav-sheet__panel" variants={sheetAnim.panel}>
                            <motion.nav
                                className="nav-sheet__list"
                                aria-label="Menu"
                                variants={sheetAnim.list}
                            >
                                {sheet.map(group => (
                                    <motion.div
                                        className="nav-sheet__group"
                                        key={group.label}
                                        variants={sheetAnim.row}
                                    >
                                        <span className="nav-sheet__group-label">{group.label}</span>
                                        <ul>
                                            {group.items.map(item => (
                                                <li key={item.href}>
                                                    <MenuLink
                                                        item={item}
                                                        className={`nav-sheet__item${
                                                            isCurrent(item.href) ? ' nav-sheet__item--current' : ''
                                                        }`}
                                                        onClick={() => setMenuOpen(false)}
                                                    >
                                                        <span className="nav-sheet__dot" />
                                                        <span>{item.name}</span>
                                                        {item.badge && <span className="nav__badge nav-sheet__badge">{item.badge}</span>}
                                                        {item.external && <span className="nav-sheet__ext">↗</span>}
                                                    </MenuLink>
                                                </li>
                                            ))}
                                        </ul>
                                    </motion.div>
                                ))}

                                <motion.div className="nav-sheet__foot" variants={sheetAnim.row}>
                                    <a className="btn btn--accent" href={contact.phoneHref}>
                                        Call {contact.phone}
                                    </a>
                                    <Link
                                        className="btn btn--ghost"
                                        to="/#contact"
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        Get a quote
                                    </Link>
                                </motion.div>
                            </motion.nav>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
